import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import SearchBar from '../../components/ui/SearchBar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

import FilterPanel from './components/FilterPanel';
import LibraryStats from './components/LibraryStats';
import VerseDetailModal from './components/VerseDetailModal';
import BulkActions from './components/BulkActions';
import DailyReviewCard from '../dashboard/components/DailyReviewCard';
import MemoryHealthCard from '../dashboard/components/MemoryHealthCard';
import BulkImportModal from './components/BulkImportModal';
import BulkExportModal from './components/BulkExportModal';
import VerseReflectionModal from './components/VerseReflectionModal';
import AddVerseModal from './components/AddVerseModal';
import AnalyticsPreviewCard from './components/AnalyticsPreviewCard';
import ExtraReviewModal from './components/ExtraReviewModal';
import Phase0VersesCard from './components/Phase0VersesCard';
import VersePDFExportModal from './components/VersePDFExportModal';
import { useAuth } from '../../contexts/AuthContext';
import { verseService } from '../../services/supabaseService';
import { schedulerService } from '../../services/schedulerService';
import { checkAndGrantAwards, checkWeeklyVerseStreak } from '../../services/awardService';
import { useAwardToast } from '../../contexts/AwardToastContext';
import { useToast } from '../../components/ToastNotification';
import { trackVerseAdded } from '../../utils/ga';
import { VerseLibrarySkeleton, FadeSlideIn } from '../../components/ui/SkeletonLoader';


const VerseLibrary = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { showAwardToast } = useAwardToast();
  const { showWarning } = useToast();
  const [verses, setVerses] = useState([]);
  const [filteredVerses, setFilteredVerses] = useState([]);
  const [selectedVerses, setSelectedVerses] = useState([]);
  const [filters, setFilters] = useState({
    version: 'all',
    status: 'all',
    category: 'all',
    hasNotes: false,
    hasLinks: false,
    dateRange: 'all',
    phase: 'all',
    phases: [] // multi-phase checkbox selection
  });
  const [isFilterCollapsed, setIsFilterCollapsed] = useState(true);
  const [selectedVerse, setSelectedVerse] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isReflectionModalOpen, setIsReflectionModalOpen] = useState(false);
  const [sortBy, setSortBy] = useState('biblicalOrder');
  const [isBulkImportOpen, setIsBulkImportOpen] = useState(false);
  const [isBulkExportOpen, setIsBulkExportOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [spacedRepetitionStats, setSpacedRepetitionStats] = useState(null);
  const [showPhaseFilter, setShowPhaseFilter] = useState(false);
  const [isAddingVerse, setIsAddingVerse] = useState(false);
  const [addError, setAddError] = useState('');
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizeError, setOptimizeError] = useState('');
  const [optimizeProgress, setOptimizeProgress] = useState({ current: 0, total: 0, isOpen: false });
  const [isAddVerseModalOpen, setIsAddVerseModalOpen] = useState(false);
  const [isExtraReviewModalOpen, setIsExtraReviewModalOpen] = useState(false);
  const [isPDFExportOpen, setIsPDFExportOpen] = useState(false);

  // Text size state — shared with Bible Reader via localStorage
  const TEXT_SIZES = [
    { key: 'sm', label: 'S', px: '11px' },
    { key: 'base', label: 'M', px: '13px' },
    { key: 'lg', label: 'L', px: '16px' },
    { key: 'xl', label: 'XL', px: '20px' },
  ];
  const [textSize, setTextSize] = useState(() => localStorage.getItem('bibleReaderTextSize') || 'base');
  const getTextSizePx = () => TEXT_SIZES?.find(s => s?.key === textSize)?.px || '13px';
  const handleTextSizeChange = (key) => {
    setTextSize(key);
    localStorage.setItem('bibleReaderTextSize', key);
  };

  // Biblical book order mapping (Genesis to Revelation)
  const biblicalBookOrder = {
    // Old Testament
    "Genesis": 1, "Exodus": 2, "Leviticus": 3, "Numbers": 4, "Deuteronomy": 5,
    "Joshua": 6, "Judges": 7, "Ruth": 8, "1 Samuel": 9, "2 Samuel": 10,
    "1 Kings": 11, "2 Kings": 12, "1 Chronicles": 13, "2 Chronicles": 14,
    "Ezra": 15, "Nehemiah": 16, "Esther": 17, "Job": 18, "Psalm": 19, "Psalms": 19,
    "Proverbs": 20, "Ecclesiastes": 21, "Song of Songs": 22, "Isaiah": 23,
    "Jeremiah": 24, "Lamentations": 25, "Ezekiel": 26, "Daniel": 27,
    "Hosea": 28, "Joel": 29, "Amos": 30, "Obadiah": 31, "Jonah": 32,
    "Micah": 33, "Nahum": 34, "Habakkuk": 35, "Zephaniah": 36, "Haggai": 37,
    "Zechariah": 38, "Malachi": 39,
    // New Testament
    "Matthew": 40, "Mark": 41, "Luke": 42, "John": 43, "Acts": 44,
    "Romans": 45, "1 Corinthians": 46, "2 Corinthians": 47, "Galatians": 48,
    "Ephesians": 49, "Philippians": 50, "Colossians": 51, "1 Thessalonians": 52,
    "2 Thessalonians": 53, "1 Timothy": 54, "2 Timothy": 55, "Titus": 56,
    "Philemon": 57, "Hebrews": 58, "James": 59, "1 Peter": 60, "2 Peter": 61,
    "1 John": 62, "2 John": 63, "3 John": 64, "Jude": 65, "Revelation": 66
  };

  // Function to extract book name from reference
  const getBookFromReference = (reference) => {
    if (!reference) return '';
    
    const bookMatch = reference?.match(/^(\d?\s?[A-Za-z]+)/);
    if (!bookMatch) return '';
    
    let bookName = bookMatch?.[1]?.trim();
    
    const bookMappings = {
      'Gen': 'Genesis', 'Ex': 'Exodus', 'Lev': 'Leviticus', 'Num': 'Numbers', 'Deut': 'Deuteronomy',
      'Josh': 'Joshua', 'Judg': 'Judges', '1 Sam': '1 Samuel', '2 Sam': '2 Samuel',
      '1 Kgs': '1 Kings', '2 Kgs': '2 Kings', '1 Chr': '1 Chronicles', '2 Chr': '2 Chronicles',
      'Neh': 'Nehemiah', 'Est': 'Esther', 'Ps': 'Psalms', 'Prov': 'Proverbs',
      'Eccl': 'Ecclesiastes', 'Song': 'Song of Songs', 'Isa': 'Isaiah', 'Jer': 'Jeremiah',
      'Lam': 'Lamentations', 'Ezek': 'Ezekiel', 'Dan': 'Daniel', 'Hos': 'Hosea',
      'Obad': 'Obadiah', 'Jon': 'Jonah', 'Mic': 'Micah', 'Nah': 'Nahum',
      'Hab': 'Habakkuk', 'Zeph': 'Zephaniah', 'Hag': 'Haggai', 'Zech': 'Zechariah',
      'Mal': 'Malachi', 'Matt': 'Matthew', 'Mk': 'Mark', 'Lk': 'Luke', 'Jn': 'John',
      'Rom': 'Romans', '1 Cor': '1 Corinthians', '2 Cor': '2 Corinthians', 'Gal': 'Galatians',
      'Eph': 'Ephesians', 'Phil': 'Philippians', 'Col': 'Colossians', '1 Thess': '1 Thessalonians',
      '2 Thess': '2 Thessalonians', '1 Tim': '1 Timothy', '2 Tim': '2 Timothy',
      'Tit': 'Titus', 'Phlm': 'Philemon', 'Heb': 'Hebrews', 'Jas': 'James',
      '1 Pet': '1 Peter', '2 Pet': '2 Peter', '1 Jn': '1 John', '2 Jn': '2 John',
      '3 Jn': '3 John', 'Rev': 'Revelation'
    };
    
    return bookMappings?.[bookName] || bookName;
  };

  // Function to get biblical order number
  const getBiblicalOrder = (reference) => {
    const bookName = getBookFromReference(reference);
    return biblicalBookOrder?.[bookName] || 999;
  };

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }
  }, [user, authLoading, navigate]);

  // Enhanced verse loading with spaced repetition data
  useEffect(() => {
    const loadVerses = async () => {
      if (!user?.id) return;

      try {
        setLoading(true);
        setError('');

        // Load verses with enhanced spaced repetition data
        const { data, error: versesError } = await verseService?.getUserVerses(
          user?.id,
          {
            status: filters?.status,
            version: filters?.version,
            phase: filters?.phase, // New phase filter
            sortBy
          }
        );

        if (versesError) {
          throw new Error(versesError?.message || 'Failed to load verses');
        }

        setVerses(data || []);
        setFilteredVerses(data || []);

        // Load spaced repetition statistics
        const { data: statsData, error: statsError } = await verseService?.getUserMemorizationStats(user?.id);
        if (!statsError && statsData) {
          setSpacedRepetitionStats(statsData);
        }

      } catch (err) {
        console.error('Verses loading error:', err);
        setError(err?.message || 'Failed to load verses');
      } finally {
        setLoading(false);
      }
    };

    if (user?.id && !authLoading) {
      loadVerses();
    }
  }, [user?.id, authLoading, filters?.status, filters?.version, filters?.phase, sortBy]);

  // Add refresh function to reload verses after updates
  const refreshVerseList = async () => {
    if (!user?.id) return;

    try {
      setError('');

      // Reload verses with current filters to get updated data
      const { data, error: versesError } = await verseService?.getUserVerses(
        user?.id,
        {
          status: filters?.status,
          version: filters?.version,
          phase: filters?.phase,
          sortBy
        }
      );

      if (versesError) {
        throw new Error(versesError?.message || 'Failed to reload verses');
      }

      setVerses(data || []);
      setFilteredVerses(data || []);

      // Also refresh spaced repetition statistics
      const { data: statsData, error: statsError } = await verseService?.getUserMemorizationStats(user?.id);
      if (!statsError && statsData) {
        setSpacedRepetitionStats(statsData);
      }

    } catch (err) {
      console.error('Verses refresh error:', err);
      setError(err?.message || 'Failed to refresh verses');
    }
  };

  const handleStartExtraReview = ({ verses: selectedVerses, rescheduleOnResults, updateLastReviewed }) => {
    navigate('/daily-review-system', {
      state: {
        extraReviewVerses: selectedVerses,
        rescheduleOnResults,
        updateLastReviewed,
        isExtraReview: true,
      }
    });
  };

  // Helper function to format relative time
  const formatRelativeTime = (date) => {
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 24) {
      return diffInHours <= 1 ? '1 hour ago' : `${diffInHours} hours ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return diffInDays === 1 ? '1 day ago' : `${diffInDays} days ago`;
    }
  };

  // Apply client-side filters (for filters not handled by the backend)
  useEffect(() => {
    let filtered = [...verses];

    // Multi-phase checkbox filter
    if (filters?.phases?.length > 0) {
      filtered = filtered?.filter(verse => filters?.phases?.includes(verse?.currentPhase ?? 0));
    }

    if (filters?.category !== 'all') {
      filtered = filtered?.filter(verse => 
        verse?.tags?.some(tag => tag?.toLowerCase()?.includes(filters?.category?.toLowerCase()))
      );
    }

    if (filters?.hasNotes) {
      filtered = filtered?.filter(verse => verse?.hasNotes);
    }

    if (filters?.hasLinks) {
      filtered = filtered?.filter(verse => verse?.hasLinks);
    }

    // Apply client-side sorting for canonical order (complex sorting)
    if (sortBy === 'biblicalOrder') {
      filtered?.sort((a, b) => {
        const orderA = getBiblicalOrder(a?.reference);
        const orderB = getBiblicalOrder(b?.reference);
        if (orderA !== orderB) {
          return orderA - orderB;
        }
        return a?.reference?.localeCompare(b?.reference, undefined, { numeric: true });
      });
    }

    setFilteredVerses(filtered);
  }, [verses, filters?.phases, filters?.category, filters?.hasNotes, filters?.hasLinks, sortBy]);

  // Enhanced stats calculation with spaced repetition data and passage support
  const stats = {
    totalVerses: verses?.length,
    totalIndividualVerses: verses?.reduce((sum, v) => sum + (v?.verseCount || 1), 0),
    masteredVerses: verses?.filter(v => v?.status === 'mastered')?.reduce((sum, v) => sum + (v?.verseCount || 1), 0),
    learningVerses: verses?.filter(v => v?.status === 'learning')?.reduce((sum, v) => sum + (v?.verseCount || 1), 0),
    newVerses: verses?.filter(v => v?.status === 'new')?.reduce((sum, v) => sum + (v?.verseCount || 1), 0),
    phase0Verses: verses?.filter(v => (v?.currentPhase ?? 0) === 0)?.reduce((sum, v) => sum + (v?.verseCount || 1), 0),
    phase1Verses: verses?.filter(v => v?.currentPhase === 1)?.reduce((sum, v) => sum + (v?.verseCount || 1), 0),
    phase2Verses: verses?.filter(v => v?.currentPhase === 2)?.reduce((sum, v) => sum + (v?.verseCount || 1), 0),
    phase3Verses: verses?.filter(v => v?.currentPhase === 3)?.reduce((sum, v) => sum + (v?.verseCount || 1), 0),
    phase4Verses: verses?.filter(v => v?.currentPhase === 4)?.reduce((sum, v) => sum + (v?.verseCount || 1), 0),
    phase5Verses: verses?.filter(v => v?.currentPhase === 5)?.reduce((sum, v) => sum + (v?.verseCount || 1), 0),
    phase6Verses: verses?.filter(v => v?.currentPhase === 6)?.reduce((sum, v) => sum + (v?.verseCount || 1), 0),
    phase7Verses: verses?.filter(v => v?.currentPhase === 7)?.reduce((sum, v) => sum + (v?.verseCount || 1), 0),
    versesDueToday: verses?.filter(v => v?.currentPhase > 0 && v?.nextReviewDate === new Date()?.toISOString()?.split('T')?.[0])?.reduce((sum, v) => sum + (v?.verseCount || 1), 0),
    versesOverdue: verses?.filter(v => v?.currentPhase > 0 && v?.isOverdue)?.reduce((sum, v) => sum + (v?.verseCount || 1), 0),
    averageInterval: spacedRepetitionStats?.averageInterval || 0,
    successRate: spacedRepetitionStats?.successRate || 0,
    totalReviews: spacedRepetitionStats?.totalReviews || 0,
    totalReviewsFromVerses: verses?.reduce((sum, v) => sum + (v?.reviewCount || 0), 0),
    totalSuccessfulReviews: verses?.reduce((sum, v) => sum + (v?.successfulReviewCount || v?.successful_reviews || 0), 0),
    totalMisses: verses?.reduce((sum, v) => sum + (v?.totalMisses || v?.total_misses || 0), 0),
    avgDaysSinceLastReview: (() => {
      const activeVerses = verses?.filter(v => (v?.currentPhase ?? 0) > 0);
      if (!activeVerses?.length) return null;
      const totalDays = activeVerses?.reduce((sum, v) => {
        const lastReviewed = v?.lastReviewedAt || v?.last_reviewed_at;
        if (!lastReviewed) return sum + 90;
        const days = Math.floor((new Date() - new Date(lastReviewed)) / (1000 * 60 * 60 * 24));
        return sum + days;
      }, 0);
      return Math.round(totalDays / activeVerses?.length);
    })(),
  };

  // Derived success rate from verse data (more accurate than DB stat)
  const computedSuccessRate = (() => {
    const totalR = stats?.totalReviewsFromVerses;
    const totalS = stats?.totalSuccessfulReviews;
    if (totalR > 0) return Math.round((totalS / totalR) * 100);
    if (spacedRepetitionStats?.successRate > 0) return Math.round(spacedRepetitionStats?.successRate);
    return null;
  })();

  const getPhaseLabel = (phase) => {
    switch (phase) {
      case 1:
        return 'Phase 1';
      case 2:
        return 'Phase 2';
      case 3:
        return 'Phase 3';
      case 4:
        return 'Phase 4';
      case 5:
        return 'Mastered';
      default:
        return 'Phase 1';
    }
  };

  const getPhaseColor = (phase) => {
    switch (phase) {
      case 0:
        return 'bg-muted text-muted-foreground';
      case 1:
        return 'bg-primary/15 text-primary';
      case 2:
        return 'bg-success/15 text-success';
      case 3:
        return 'bg-warning/15 text-warning';
      case 4:
        return 'bg-warning/20 text-warning';
      case 5:
        return 'bg-accent/15 text-accent-foreground';
      case 6:
        return 'bg-destructive/15 text-destructive';
      case 7:
        return 'bg-success/20 text-success';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  // Helper function to format interval display
  const formatInterval = (interval, reviewCount) => {
    if (interval === null || interval === undefined) return '—';
    if (reviewCount === 0 || reviewCount === null || reviewCount === undefined) return '—';
    if (interval < 1) return `${Math.round(interval * 24)}h`;
    if (interval === 1) return '1 day';
    if (interval < 30) return `${Math.round(interval)} days`;
    if (interval < 90) return `${Math.round(interval / 30)} month${interval >= 60 ? 's' : ''}`;
    return `${Math.round(interval / 30)} months`;
  };

  const handleSearch = (query) => {
    const searchResults = verses?.filter(verse =>
      verse?.reference?.toLowerCase()?.includes(query?.toLowerCase()) ||
      verse?.text?.toLowerCase()?.includes(query?.toLowerCase()) ||
      verse?.tags?.some(tag => tag?.toLowerCase()?.includes(query?.toLowerCase()))
    );
    setFilteredVerses(searchResults);
  };

  const handleVerseSelect = (verse) => {
    setSelectedVerses(prev => {
      const isSelected = prev?.some(v => v?.id === verse?.id);
      if (isSelected) {
        return prev?.filter(v => v?.id !== verse?.id);
      } else {
        return [...prev, verse];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedVerses?.length === filteredVerses?.length) {
      setSelectedVerses([]);
    } else {
      setSelectedVerses([...filteredVerses]);
    }
  };

  const handleClearSelection = () => {
    setSelectedVerses([]);
  };

  const handlePractice = (verse) => {
    navigate('/memorization-practice', { state: { selectedVerse: verse } });
  };

  const handleEditNotes = (verse) => {
    setSelectedVerse(verse);
    setIsDetailModalOpen(true);
  };

  const handleLinkVerses = (verse) => {
    console.log('Link verses for:', verse?.reference);
  };

  const handleToggleLibrary = (verse) => {
    setVerses(prev => prev?.map(v => 
      v?.id === verse?.id 
        ? { ...v, isInLibrary: !v?.isInLibrary }
        : v
    ));
  };

  const handleViewDetails = (verse) => {
    if (!user) {
      showWarning('Please sign in to view and edit verse details.', 'Sign In Required');
      navigate('/login', { state: { from: '/verse-library' } });
      return;
    }
    setSelectedVerse(verse);
    setIsDetailModalOpen(true);
  };

  const handleSaveVerse = async (verseId, dbUpdates, updatedVerse) => {
    try {
      const { data, error } = await verseService?.updateVerse(verseId, dbUpdates);

      if (error) {
        throw new Error(error?.message || 'Failed to update verse');
      }

      // Update local state with the full updated verse data
      setVerses(prev => prev?.map(v => 
        v?.id === verseId ? { ...v, ...updatedVerse } : v
      ));

      // Automatically refresh the verse list to ensure data consistency
      await refreshVerseList();

      setError('Verse updated successfully!');
      setTimeout(() => setError(''), 3000);

    } catch (err) {
      console.error('Failed to update verse:', err);
      setError(err?.message || 'Failed to update verse');
    }
  };

  const handleDeleteVerse = async (verseId) => {
    try {
      const { data, error } = await verseService?.deleteVerse(verseId);

      if (error) {
        throw new Error(error?.message || 'Failed to delete verse');
      }

      // Remove verse from local state
      setVerses(prev => prev?.filter(v => v?.id !== verseId));
      setFilteredVerses(prev => prev?.filter(v => v?.id !== verseId));
      setSelectedVerses(prev => prev?.filter(v => v?.id !== verseId));

      setError('Verse deleted successfully!');
      setTimeout(() => setError(''), 3000);

    } catch (err) {
      console.error('Failed to delete verse:', err);
      setError(err?.message || 'Failed to delete verse');
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedVerses?.length === 0) return;

    try {
      switch (action) {
        case 'delete':
          await Promise.all(
            selectedVerses?.map(verseId => verseService?.deleteVerse(verseId))
          );
          setVerses(prev => prev?.filter(v => !selectedVerses?.includes(v?.id)));
          setSelectedVerses([]);
          break;
        
        case 'markMastered':
          await Promise.all(
            selectedVerses?.map(verseId => verseService?.updateVerse(verseId, { status: 'mastered' }))
          );
          setVerses(prev => prev?.map(v => 
            selectedVerses?.includes(v?.id) ? { ...v, status: 'mastered' } : v
          ));
          break;
        
        default:
          break;
      }
    } catch (error) {
      console.error('Bulk action error:', error);
      setError('Failed to perform bulk action');
    }
  };

  // Handler for optimize schedule
  const handleOptimizeSchedule = async (optimizeAll = true) => {
    if (!user?.id) return;

    setIsOptimizing(true);
    setOptimizeError('');
    setOptimizeProgress({ current: 0, total: 0, isOpen: true });

    try {
      // Call the scheduler service to optimize all verses
      const result = await schedulerService?.optimizeSchedule(user?.id, optimizeAll, (current, total) => {
        setOptimizeProgress(prev => ({ ...prev, current, total }));
      });

      if (result?.error) {
        setOptimizeError(result?.error);
        setOptimizeProgress(prev => ({ ...prev, isOpen: false }));
        return;
      }

      // Refresh the verse data to show updated schedule
      await refreshVerseList();

      // Show success message
      setError('');
      setOptimizeProgress(prev => ({ ...prev, isOpen: false }));
    } catch (error) {
      console.error('Error optimizing schedule:', error);
      setOptimizeError('Failed to optimize schedule. Please try again.');
      setOptimizeProgress(prev => ({ ...prev, isOpen: false }));
    } finally {
      setIsOptimizing(false);
    }
  };

  const handleBulkImport = async (importData) => {
    try {
      setLoading(true);
      setError('');

      // Handle different import formats with proper validation
      const { verses: importedVerses, imported, failed, total, phase, format } = importData;
      
      // Validate the import data structure
      if (!importedVerses || !Array.isArray(importedVerses)) {
        throw new Error('Invalid import data format - verses array is required');
      }

      if (importedVerses?.length === 0) {
        throw new Error('No valid verses to import');
      }

      // Add this block - Define phaseOptions array
      const phaseOptions = [
        { value: 1, label: 'Phase 1 - Initial Learning (1 day)' },
        { value: 2, label: 'Phase 2 - Short Term (3 days)' },
        { value: 3, label: 'Phase 3 - Building Memory (7 days)' },
        { value: 4, label: 'Phase 4 - Medium Term (14 days)' },
        { value: 5, label: 'Phase 5 - Long Term (30 days)' },
        { value: 6, label: 'Phase 6 - Extended Memory (60 days)' },
        { value: 7, label: 'Phase 7 - Mastered (90+ days)' }
      ];

      // Import verses to database using the verses service
      const importPromises = importedVerses?.map(async (verseData) => {
        try {
          const { error } = await verseService?.createVerse({
            ...verseData,
            user_id: user?.id,
            created_at: new Date()?.toISOString(),
            next_review_date: new Date()?.toISOString()?.split('T')?.[0], // Set next review to today for new verses
            last_reviewed_at: null,
            current_interval: 1.0, // Start with 1-day interval
            last_interval: 1.0,
            ease_factor: 2.5, // Default ease factor
            consecutive_correct: 0,
            total_reviews: 0,
            total_misses: 0
          });

          if (error) {
            console.error(`Failed to import verse ${verseData?.reference}:`, error);
            throw new Error(`Failed to import verse ${verseData?.reference}: ${error?.message}`);
          }

          return verseData;
        } catch (err) {
          console.error(`Error importing verse ${verseData?.reference}:`, err);
          throw err;
        }
      });

      try {
        await Promise.all(importPromises);
        
        // After all verses are inserted, run optimizeSchedule so they are
        // distributed across the correct review window instead of all
        // landing on today's date.
        if (importedVerses?.length > 0 && user?.id) {
          try {
            await schedulerService?.optimizeSchedule(user?.id, true);
          } catch (schedErr) {
            console.warn('Post-import schedule optimization failed (non-fatal):', schedErr);
          }

          // BUG 4: Check weekly verse streak awards after bulk import
          try {
            const { supabase: _supabase } = await import('../../lib/supabase');
            const weeklyAwards = await checkWeeklyVerseStreak(user?.id, _supabase);
            weeklyAwards?.forEach(a => showAwardToast(a));
          } catch (weeklyErr) {
            console.warn('Weekly verse streak check failed (non-fatal):', weeklyErr);
          }
        }

        // Refresh verses list to show imported verses
        await refreshVerseList();

        // Show success message with import details
        const successMessage = `Successfully imported ${importedVerses?.length} verses into ${phaseOptions?.find(p => p?.value === phase)?.label || 'Phase ' + phase}!`;
        
        if (failed && failed?.length > 0) {
          setError(successMessage + ` ${failed?.length} verses failed validation and were not imported.`);
        } else {
          setError(successMessage);
        }
        
        setTimeout(() => setError(''), 5000);

      } catch (importError) {
        console.error('Import process failed:', importError);
        throw importError;
      }

    } catch (err) {
      console.error('Bulk import failed:', err);
      setError(err?.message || 'Bulk import failed. Please check your data format and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReflect = (verse) => {
    if (!user) {
      showWarning('Please sign in to add reflections.', 'Sign In Required');
      navigate('/login', { state: { from: '/verse-library' } });
      return;
    }
    setSelectedVerse(verse);
    setIsReflectionModalOpen(true);
  };

  const handleSaveReflection = async (verseId, reflectionData) => {
    try {
      const { data, error } = await verseService?.saveReflection(verseId, {
        ...reflectionData,
        userId: user?.id
      });

      if (error) {
        throw new Error(error?.message || 'Failed to save reflection');
      }

      // Award checks for reflection
      if (user?.id) {
        try {
          const { count: totalReflections } = await import('../../lib/supabase')?.then(m =>
            m?.supabase?.from('verse_reflections')?.select('id', { count: 'exact', head: true })?.eq('user_id', user?.id)
          );
          const charCount = (reflectionData?.insights?.length || 0) +
            (reflectionData?.illustrations?.length || 0) +
            (reflectionData?.testimonies?.length || 0) +
            (reflectionData?.actions?.length || 0);
          const [writtenAwards, milestoneAwards] = await Promise.all([
            checkAndGrantAwards(user?.id, 'REFLECTION_WRITTEN', { totalReflections: totalReflections || 1, charCount }),
            checkAndGrantAwards(user?.id, 'REFLECTION_MILESTONE', { totalReflections: totalReflections || 1 })
          ]);
          [...writtenAwards, ...milestoneAwards]?.forEach(a => showAwardToast(a));
        } catch (awardErr) {
          console.warn('Award check failed:', awardErr);
        }
      }

      // Refresh verses to show updated reflection count
      const { data: updatedVerses, error: versesError } = await verseService?.getUserVerses(
        user?.id,
        {
          status: filters?.status,
          version: filters?.version,
          phase: filters?.phase,
          sortBy
        }
      );

      if (!versesError && updatedVerses) {
        const transformedVerses = updatedVerses?.map(verse => ({
          id: verse?.id,
          reference: verse?.reference,
          text: verse?.text,
          version: verse?.bible_version?.toUpperCase(),
          status: verse?.status,
          progress: verse?.progress_percentage,
          phase: verse?.current_phase || 1,
          reviewCount: verse?.review_count || 0,
          tags: verse?.tags || [],
          notes: verse?.notes || '',
          dateAdded: verse?.created_at?.split('T')?.[0],
          lastStudied: verse?.last_reviewed_at ? formatRelativeTime(new Date(verse?.last_reviewed_at)) : 'Never',
          isInLibrary: true,
          hasNotes: Boolean(verse?.notes),
          hasLinks: false,
          reflections: verse?.verse_reflections?.map(reflection => ({
            id: reflection?.id,
            isPrivate: reflection?.is_private,
            createdAt: reflection?.created_at,
            reflections: {
              insights: reflection?.insights,
              illustrations: reflection?.illustrations,
              testimonies: reflection?.testimonies,
              actions: reflection?.actions,
              relationships: reflection?.relationships
            }
          })) || []
        })) || [];
        setVerses(transformedVerses);
      }

      setError('Reflection saved successfully!');
      setTimeout(() => setError(''), 3000);

    } catch (err) {
      console.error('Failed to save reflection:', err);
      setError(err?.message || 'Failed to save reflection');
    }
  };

  const getBucketColor = (bucket) => {
    switch (bucket) {
      case 'Daily':
        return 'bg-success/15 text-success';
      case 'Weekly':
        return 'bg-primary/15 text-primary';
      case 'Monthly':
        return 'bg-warning/15 text-warning';
      case 'Quarterly':
        return 'bg-accent/15 text-accent-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getProgressColor = (progress) => {
    if (progress >= 80) return 'text-success';
    if (progress >= 50) return 'text-warning';
    return 'text-destructive';
  };

  const handleRowClick = (verse) => {
    setSelectedVerse(verse);
    setIsDetailModalOpen(true);
  };

  // Show loading state
  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <VerseLibrarySkeleton />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-4 py-6 pb-20 md:pb-6">
        <FadeSlideIn>
          {/* Error Display */}
          {error && (
            <div className="bg-destructive/10 border border-destructive/30 text-destructive px-4 py-3 rounded-lg mb-6 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name="AlertCircle" size={18} />
                <span>{error}</span>
              </div>
              <button
                onClick={() => setError('')}
                className="text-destructive hover:text-destructive/70 ml-auto"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
          )}

          {optimizeError && (
            <div className="bg-destructive/10 border border-destructive/30 text-destructive px-4 py-3 rounded-lg mb-6 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name="AlertCircle" size={18} />
                <span>{optimizeError}</span>
              </div>
              <button
                onClick={() => setOptimizeError('')}
                className="text-destructive hover:text-destructive/70 ml-auto"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
          )}

          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
            <div className="mb-4 lg:mb-0">
              <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                My Verse Library
              </h1>
              <p className="text-muted-foreground">
                Manage and organize your personal collection of Bible verses for memorization and study.
              </p>
            </div>
            
            <div className="flex flex-wrap items-center gap-2 lg:gap-3">
              <Button
                variant="outline"
                onClick={() => navigate('/study-analytics-dashboard')}
                iconName="BarChart2"
                iconPosition="left"
                className="text-sm rounded-full"
              >
                <span className="hidden sm:inline">Analytics</span>
                <span className="sm:hidden">Analytics</span>
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsBulkImportOpen(true)}
                iconName="Upload"
                iconPosition="left"
                className="text-sm rounded-full"
              >
                <span className="hidden sm:inline">Import Verses</span>
                <span className="sm:hidden">Import</span>
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsBulkExportOpen(true)}
                iconName="Download"
                iconPosition="left"
                className="text-sm rounded-full"
              >
                <span className="hidden sm:inline">Export Verses</span>
                <span className="sm:hidden">Export</span>
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsPDFExportOpen(true)}
                iconName="FileText"
                iconPosition="left"
                className="text-sm rounded-full border-primary/40 text-primary hover:bg-primary/5"
              >
                <span className="hidden sm:inline">Export PDF Report</span>
                <span className="sm:hidden">PDF</span>
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsAddVerseModalOpen(true)}
                iconName="Plus"
                iconPosition="left"
                className="text-sm rounded-full"
              >
                <span className="hidden sm:inline">Add Verses</span>
                <span className="sm:hidden">Add</span>
              </Button>
              <Button
                variant="outline"
                onClick={() => handleOptimizeSchedule(true)}
                iconName="Calendar"
                iconPosition="left"
                className="text-sm rounded-full"
                disabled={isOptimizing || verses?.length === 0}
              >
                <span className="hidden sm:inline">{isOptimizing ? 'Optimizing...' : 'Optimize Schedule'}</span>
                <span className="sm:hidden">{isOptimizing ? '...' : 'Optimize'}</span>
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/memorization-practice')}
                iconName="Brain"
                iconPosition="left"
                className="text-sm rounded-full"
              >
                <span className="hidden sm:inline">Memorize</span>
                <span className="sm:hidden">Memorize</span>
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsExtraReviewModalOpen(true)}
                iconName="RefreshCw"
                iconPosition="left"
                className="text-sm rounded-full"
              >
                <span className="hidden sm:inline">Extra Review</span>
                <span className="sm:hidden">Extra Review</span>
              </Button>
            </div>
          </div>

          {/* Enhanced Daily Review Section with Spaced Repetition Info */}
          <div className="mb-8">
            <DailyReviewCard onExtraReview={() => setIsExtraReviewModalOpen(true)} />

            {/* Memory Health Card - just below daily review */}
            <div className="mt-4">
              <MemoryHealthCard
                verses={verses}
                onExtraReview={() => setIsExtraReviewModalOpen(true)}
              />
            </div>

            {/* Phase 0 Verses Card - right under daily review */}
            <Phase0VersesCard verses={verses} />

            {/* Analytics Preview Card - cycles through graphs */}
            <AnalyticsPreviewCard />
            
            {/* Spaced Repetition Quick Stats */}
            <div className="mt-4 bg-card border border-border rounded-lg p-4">
              <h3 className="text-lg font-semibold text-foreground mb-3">Spaced Repetition Overview</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-destructive">
                    {spacedRepetitionStats?.versesDueToday ?? stats?.versesDueToday}
                  </div>
                  <div className="text-sm text-muted-foreground">Due Today</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-warning">
                    {spacedRepetitionStats?.versesOverdue ?? stats?.versesOverdue}
                  </div>
                  <div className="text-sm text-muted-foreground">Overdue</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">{stats?.phase7Verses}</div>
                  <div className="text-sm text-muted-foreground">Mastered</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">
                    {stats?.avgDaysSinceLastReview !== null ? `${stats?.avgDaysSinceLastReview}d` : '—'}
                  </div>
                  <div className="text-sm text-muted-foreground">Avg Days Since Review</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-accent">
                    {computedSuccessRate !== null ? `${computedSuccessRate}%` : '—'}
                  </div>
                  <div className="text-sm text-muted-foreground">Success Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-foreground">
                    {stats?.totalReviewsFromVerses > 0 ? stats?.totalReviewsFromVerses : (stats?.totalReviews || '—')}
                  </div>
                  <div className="text-sm text-muted-foreground">Total Reviews</div>
                </div>
              </div>
              {/* Additional metrics row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-border">
                <div className="text-center">
                  <div className="text-xl font-bold text-muted-foreground">{stats?.phase0Verses}</div>
                  <div className="text-xs text-muted-foreground">Not Started</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-destructive">{stats?.totalMisses}</div>
                  <div className="text-xs text-muted-foreground">Total Misses</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-success">{stats?.totalSuccessfulReviews}</div>
                  <div className="text-xs text-muted-foreground">Successful Reviews</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-foreground">{stats?.totalVerses}</div>
                  <div className="text-xs text-muted-foreground">Total Verses</div>
                </div>
              </div>
            </div>
          </div>

          {/* Search and Controls */}
          <div className="flex flex-col lg:flex-row gap-4 mb-6">
            <div className="flex-1">
              <SearchBar
                onSearch={handleSearch}
                placeholder="Search verses by reference, text, or tags..."
                className="w-full"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e?.target?.value)}
                className="px-3 py-2 bg-input border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring min-w-0 flex-shrink-0"
              >
                <option value="biblicalOrder">Canonical Order</option>
                <option value="reference">Reference</option>
                <option value="progress">Progress</option>
                <option value="status">Status</option>
                <option value="phase">Spaced Rep Phase</option>
                <option value="interval">Current Interval</option>
                <option value="nextReview">Next Review Date</option>
                <option value="dateAdded">Date Added</option>
              </select>

              {/* Text Size Control */}
              <div className="flex items-center gap-0 border border-border rounded-md overflow-hidden bg-background flex-shrink-0">
                <button
                  onClick={() => {
                    const idx = TEXT_SIZES?.findIndex(s => s?.key === textSize);
                    if (idx > 0) handleTextSizeChange(TEXT_SIZES?.[idx - 1]?.key);
                  }}
                  title="Decrease text size"
                  disabled={TEXT_SIZES?.findIndex(s => s?.key === textSize) === 0}
                  className="px-2.5 py-1.5 font-medium transition-colors min-w-[28px] min-h-[32px] leading-none text-sm text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  −
                </button>
                <span
                  className="px-2 py-1.5 min-w-[28px] min-h-[32px] leading-none flex items-center justify-center font-medium text-foreground select-none border-x border-border"
                  style={{ fontSize: getTextSizePx() }}
                >
                  T
                </span>
                <button
                  onClick={() => {
                    const idx = TEXT_SIZES?.findIndex(s => s?.key === textSize);
                    if (idx < TEXT_SIZES?.length - 1) handleTextSizeChange(TEXT_SIZES?.[idx + 1]?.key);
                  }}
                  title="Increase text size"
                  disabled={TEXT_SIZES?.findIndex(s => s?.key === textSize) === TEXT_SIZES?.length - 1}
                  className="px-2.5 py-1.5 font-medium transition-colors min-w-[28px] min-h-[32px] leading-none text-sm text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  +
                </button>
              </div>

              <Button
                variant="outline"
                onClick={() => setIsFilterCollapsed(!isFilterCollapsed)}
                iconName={isFilterCollapsed ? "ChevronDown" : "ChevronUp"}
                iconPosition="left"
                className="text-sm flex-shrink-0"
              >
                <span className="hidden md:inline">{isFilterCollapsed ? 'Show Filters' : 'Hide Filters'}</span>
                <span className="md:hidden">Filters</span>
              </Button>
            </div>
          </div>

          {/* Bulk Actions */}
          <BulkActions
            selectedVerses={selectedVerses}
            onClearSelection={handleClearSelection}
            onBulkAction={handleBulkAction}
            className="mb-4"
          />

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Enhanced Filters Sidebar with Phase Filter */}
            {!isFilterCollapsed && (
              <div className="lg:col-span-1">
                <div className="space-y-4">
                  <FilterPanel
                    filters={{...filters, phase: filters?.phase || 'all', phases: filters?.phases || []}}
                    onFiltersChange={(newFilters) => setFilters({...newFilters})}
                    onClearFilters={() => setFilters({
                      version: 'all',
                      category: 'all',
                      phase: 'all',
                      phases: [],
                      hasNotes: false,
                      hasLinks: false,
                      dateRange: 'all'
                    })}
                    isCollapsed={false}
                    onToggleCollapse={() => setIsFilterCollapsed(!isFilterCollapsed)}
                    showPhaseFilter={true}
                  />
                  
                  <LibraryStats stats={stats} spacedRepetitionStats={spacedRepetitionStats} />
                </div>
              </div>
            )}

            {/* Enhanced Verses Table with Spaced Repetition Data */}
            <div className={isFilterCollapsed ? "lg:col-span-4" : "lg:col-span-3"}>
              {/* Results Header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
                <div className="flex items-center gap-4">
                  <p className="text-sm text-muted-foreground">
                    {filteredVerses?.length} of {verses?.length} verses
                  </p>
                  
                  {filteredVerses?.length > 0 && (
                    <button
                      onClick={handleSelectAll}
                      className="flex items-center gap-1 text-sm text-primary hover:text-primary/80 transition-colors duration-200"
                    >
                      <Icon name="CheckSquare" size={14} />
                      <span>
                        {selectedVerses?.length === filteredVerses?.length ? 'Deselect All' : 'Select All'}
                      </span>
                    </button>
                  )}
                </div>
              </div>

              {/* Enhanced Verses Table Display */}
              {filteredVerses?.length > 0 ? (
                <div className="bg-card border border-border rounded-lg overflow-hidden">
                  {/* Fixed-height scrollable container with frozen header */}
                  <div className="overflow-auto" style={{ maxHeight: '480px' }}>
                    <table className="w-full">
                      <thead className="bg-muted/50 border-b border-border sticky top-0 z-10">
                        <tr>
                          <th className="text-left p-3 text-sm font-semibold text-foreground">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={handleSelectAll}
                                className="flex items-center justify-center w-5 h-5 border border-border rounded transition-colors duration-200 hover:bg-muted flex-shrink-0"
                              >
                                {selectedVerses?.length === filteredVerses?.length && filteredVerses?.length > 0 && (
                                  <Icon name="Check" size={12} color="var(--color-primary)" />
                                )}
                              </button>
                              <span>Reference</span>
                            </div>
                          </th>
                          <th className="text-right p-3 text-sm font-semibold text-foreground w-24">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredVerses?.map((verse, index) => (
                          <VerseRow
                            key={verse?.id}
                            verse={verse}
                            isSelected={selectedVerses?.some(v => v?.id === verse?.id)}
                            getPhaseColor={getPhaseColor}
                            getProgressColor={getProgressColor}
                            handleVerseSelect={handleVerseSelect}
                            handleRowClick={handleRowClick}
                            handleViewDetails={handleViewDetails}
                            handlePractice={handlePractice}
                            handleReflect={handleReflect}
                            handleDeleteVerse={handleDeleteVerse}
                            textSizePx={getTextSizePx()}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="flex items-center justify-center w-16 h-16 bg-muted rounded-full mx-auto mb-4">
                    <Icon name="BookOpen" size={24} className="text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                    {verses?.length === 0 ? 'No verses in your library yet' : 'No verses found'}
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    {verses?.length === 0 
                      ? 'Start building your Scripture library by adding verses for memorization.'
                      : 'Try adjusting your filters or search terms.'
                    }
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => setIsAddVerseModalOpen(true)}
                    iconName="Plus"
                    iconPosition="left"
                  >
                    {verses?.length === 0 ? 'Add Your First Verse' : 'Add More Verses'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </FadeSlideIn>
      </div>
      {/* Modals */}
      <VerseDetailModal
        verse={selectedVerse}
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedVerse(null);
        }}
        onSave={handleSaveVerse}
        onUpdate={refreshVerseList}
        onPractice={handlePractice}
        onLinkVerses={handleLinkVerses}
        onDelete={handleDeleteVerse}
      />
      <VerseReflectionModal
        isOpen={isReflectionModalOpen}
        onClose={() => setIsReflectionModalOpen(false)}
        verse={selectedVerse}
        onSave={handleSaveReflection}
      />
      <BulkImportModal
        isOpen={isBulkImportOpen}
        onClose={() => setIsBulkImportOpen(false)}
        onImport={handleBulkImport}
      />
      <BulkExportModal
        isOpen={isBulkExportOpen}
        onClose={() => setIsBulkExportOpen(false)}
        verses={filteredVerses?.length > 0 ? filteredVerses : verses}
      />
      <AddVerseModal
        isOpen={isAddVerseModalOpen}
        onClose={() => setIsAddVerseModalOpen(false)}
        onVerseAdded={() => {
          trackVerseAdded('', '');
          refreshVerseList();
        }}
        userId={user?.id}
      />
      <ExtraReviewModal
        isOpen={isExtraReviewModalOpen}
        onClose={() => setIsExtraReviewModalOpen(false)}
        verses={verses}
        onStartReview={handleStartExtraReview}
      />
      <VersePDFExportModal
        isOpen={isPDFExportOpen}
        onClose={() => setIsPDFExportOpen(false)}
        verses={filteredVerses?.length > 0 ? filteredVerses : verses}
      />

      {/* Optimize All Progress Modal */}
      {optimizeProgress?.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl shadow-2xl p-8 w-full max-w-sm mx-4 flex flex-col items-center gap-5">
            <div className="flex items-center justify-center w-14 h-14 rounded-full bg-primary/10">
              <Icon name="Calendar" size={28} className="text-primary animate-pulse" />
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold text-foreground mb-1">Optimizing Schedule</h3>
              <p className="text-sm text-muted-foreground">
                Redistributing your verse reviews for a smoother schedule…
              </p>
            </div>
            {optimizeProgress?.total > 0 ? (
              <>
                <div className="w-full">
                  <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
                    <span>Progress</span>
                    <span>{optimizeProgress?.current} / {optimizeProgress?.total}</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2.5 overflow-hidden">
                    <div
                      className="h-2.5 rounded-full bg-primary transition-all duration-300 ease-out"
                      style={{ width: `${Math.round((optimizeProgress?.current / optimizeProgress?.total) * 100)}%` }}
                    />
                  </div>
                  <div className="text-center text-xs text-muted-foreground mt-1.5">
                    {Math.round((optimizeProgress?.current / optimizeProgress?.total) * 100)}% complete
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon name="Loader2" size={16} className="animate-spin" />
                <span>Fetching verses…</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const VerseRow = ({ verse, isSelected, getPhaseColor, getProgressColor, handleVerseSelect, handleRowClick, handleViewDetails, handlePractice, handleReflect, handleDeleteVerse, textSizePx }) => {
  const [expanded, setExpanded] = React.useState(false);
  const [progressAnimated, setProgressAnimated] = React.useState(false);

  const isPhase0 = (verse?.currentPhase ?? 0) === 0;
  const lastReviewedRaw = verse?.lastReviewedAt || verse?.last_reviewed_at;
  const daysSinceReview = lastReviewedRaw
    ? Math.floor((new Date() - new Date(lastReviewedRaw)) / (1000 * 60 * 60 * 24))
    : null;

  // Reviews needed until next phase
  const phaseRequirements = { 1: 30, 2: 10, 3: 6, 4: 3, 5: 3, 6: 2, 7: 16 };
  const currentPhase = verse?.currentPhase ?? 0;
  const reviewsInPhase = verse?.successfulReviewsInPhase || verse?.successful_reviews_in_phase || 0;
  const requiredForNextPhase = phaseRequirements?.[currentPhase] || null;
  const reviewsNeeded = requiredForNextPhase !== null ? Math.max(0, requiredForNextPhase - reviewsInPhase) : null;

  const handleExpand = (e) => {
    e?.stopPropagation();
    const next = !expanded;
    setExpanded(next);
    if (next) {
      setTimeout(() => setProgressAnimated(true), 50);
    } else {
      setProgressAnimated(false);
    }
  };

  return (
    <React.Fragment>
      <tr
        className={`border-b border-border hover:bg-muted/20 transition-colors duration-200 ${
          isSelected ? 'bg-primary/5' : ''
        } ${!isPhase0 && verse?.isOverdue ? 'bg-destructive/5' : ''} ${
          isPhase0 ? 'bg-muted/30' : ''
        }`}
      >
        <td className="p-3">
          <div className="flex items-start gap-2">
            {/* Selection checkbox */}
            <button
              onClick={(e) => {
                e?.stopPropagation();
                handleVerseSelect(verse);
              }}
              className="flex items-center justify-center w-5 h-5 border border-border rounded transition-colors duration-200 hover:bg-muted flex-shrink-0 mt-0.5"
            >
              {isSelected && <Icon name="Check" size={12} color="var(--color-primary)" />}
            </button>

            {/* Expand/collapse arrow button */}
            <button
              onClick={handleExpand}
              className="flex items-center justify-center w-5 h-5 flex-shrink-0 mt-0.5 text-muted-foreground hover:text-foreground transition-colors duration-200"
              title={expanded ? 'Hide details' : 'Show details'}
            >
              <Icon name={expanded ? 'ChevronDown' : 'ChevronRight'} size={16} />
            </button>

            {/* Reference + Phase */}
            <div className="min-w-0 flex-1 cursor-pointer" onClick={() => handleRowClick(verse)}>
              <div className={`relative group/ref font-semibold text-sm break-words ${isPhase0 ? 'text-muted-foreground' : 'text-foreground'}`}>
                {verse?.reference}
                {verse?.text && (
                  <div className="absolute left-0 top-full mt-1 z-50 w-72 max-w-xs pointer-events-none
                    opacity-0 group-hover/ref:opacity-100 translate-y-1 group-hover/ref:translate-y-0
                    transition-all duration-200 ease-out hidden md:block">
                    <div className="bg-popover border border-border rounded-lg shadow-xl p-3">
                      <div className="absolute -top-1.5 left-4 w-3 h-3 bg-popover border-l border-t border-border rotate-45" />
                      <p className="text-sm text-popover-foreground leading-relaxed italic">
                        "{verse?.text?.length > 200 ? verse?.text?.slice(0, 200) + '…' : verse?.text}"
                      </p>
                      <p className="text-xs text-muted-foreground mt-1.5 font-medium not-italic">
                        — {verse?.reference} ({verse?.bible_version?.toUpperCase()})
                      </p>
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-0.5">
                <span className={`inline-block text-xs px-1.5 py-0.5 rounded font-medium ${getPhaseColor(verse?.currentPhase ?? 0)}`}>
                  {isPhase0 ? 'Not Started' : `Phase ${verse?.currentPhase}`}
                </span>
              </div>
              {!isPhase0 && verse?.isOverdue && (
                <div className="flex items-center gap-1 mt-0.5">
                  <Icon name="AlertCircle" size={12} className="text-destructive" />
                  <span className="text-xs text-destructive">
                    {Math.abs(verse?.daysUntilReview)}d overdue
                  </span>
                </div>
              )}
            </div>
          </div>
        </td>

        {/* Actions column */}
        <td className="p-3">
          <div className="flex items-center justify-end gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e?.stopPropagation();
                handleViewDetails(verse);
              }}
              title="Verse Detail"
              className="h-8 w-8 flex-shrink-0"
            >
              <Icon name="FileText" size={14} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e?.stopPropagation();
                handlePractice(verse);
              }}
              title="Start Practice"
              className="h-8 w-8 flex-shrink-0"
            >
              <Icon name="Brain" size={14} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e?.stopPropagation();
                handleReflect(verse);
              }}
              title="Guided Reflection"
              className="h-8 w-8 flex-shrink-0"
            >
              <Icon name="BookOpen" size={14} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e?.stopPropagation();
                if (window.confirm(`Delete "${verse?.reference}" from your library?`)) {
                  handleDeleteVerse(verse?.id);
                }
              }}
              title="Delete Verse"
              className="h-8 w-8 flex-shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <Icon name="Trash2" size={14} />
            </Button>
          </div>
        </td>
      </tr>
      {/* Expandable detail row */}
      {expanded && (
        <tr className={`border-b border-border ${isPhase0 ? 'bg-muted/20' : 'bg-muted/10'}`}>
          <td colSpan={2} className="px-4 py-2">
            {/* Verse Text */}
            {verse?.text && (
              <p
                className="italic text-foreground leading-relaxed mb-3 pb-3 border-b border-border/50"
                style={{ fontSize: textSizePx }}
              >
                "{verse?.text}"
              </p>
            )}
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-x-3 gap-y-2 text-xs">
              {/* Phase */}
              <div>
                <div className="text-muted-foreground mb-0.5">Phase</div>
                <span className={`inline-block text-xs px-1.5 py-0.5 rounded font-medium ${getPhaseColor(verse?.currentPhase ?? 0)}`}>
                  {isPhase0 ? 'Not Started' : `Phase ${verse?.currentPhase}`}
                </span>
              </div>

              {/* Last Reviewed */}
              <div>
                <div className="text-muted-foreground mb-0.5">Last Reviewed</div>
                <span className={`font-medium ${
                  daysSinceReview === null ? 'text-muted-foreground' :
                  daysSinceReview === 0 ? 'text-success' :
                  daysSinceReview <= 7 ? 'text-foreground' :
                  daysSinceReview <= 30 ? 'text-warning' : 'text-destructive'
                }`}>
                  {daysSinceReview === null ? 'Never' :
                   daysSinceReview === 0 ? 'Today' :
                   `${daysSinceReview}d ago`}
                </span>
              </div>

              {/* Next Review */}
              <div>
                <div className="text-muted-foreground mb-0.5">Next Review</div>
                {isPhase0 ? (
                  <span className="text-muted-foreground">—</span>
                ) : (
                  <span className={`font-medium ${verse?.isOverdue ? 'text-destructive' : 'text-foreground'}`}>
                    {verse?.nextReviewDate
                      ? new Date(verse?.nextReviewDate)?.toLocaleDateString()
                      : 'Not set'}
                  </span>
                )}
              </div>

              {/* Reviews */}
              <div>
                <div className="text-muted-foreground mb-0.5">Reviews</div>
                <span className="font-medium text-foreground">{verse?.reviewCount || 0}</span>
              </div>

              {/* Successful Reviews */}
              <div>
                <div className="text-muted-foreground mb-0.5">Successful</div>
                <span className="font-medium text-success">
                  {verse?.successfulReviewCount || verse?.successful_reviews || 0}
                </span>
              </div>

              {/* Reviews needed until next phase */}
              <div>
                <div className="text-muted-foreground mb-0.5">Until Next Phase</div>
                {isPhase0 || reviewsNeeded === null ? (
                  <span className="text-muted-foreground">—</span>
                ) : reviewsNeeded === 0 ? (
                  <span className="font-medium text-success">Ready!</span>
                ) : (
                  <span className="font-medium text-primary">{reviewsNeeded} left</span>
                )}
              </div>

              {/* Progress Graph — animated on reveal, full width */}
              <div className="col-span-3 sm:col-span-6">
                <div className="text-muted-foreground mb-1">Progress</div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                    <div
                      className="h-2 rounded-full transition-all duration-700 ease-out"
                      style={{
                        width: progressAnimated ? `${verse?.progress || 0}%` : '0%',
                        background: verse?.progress >= 80
                          ? 'var(--color-success)'
                          : verse?.progress >= 50
                          ? 'var(--color-warning)'
                          : 'var(--color-primary)',
                      }}
                    />
                  </div>
                  <span className={`font-semibold whitespace-nowrap ${getProgressColor(verse?.progress)}`}>
                    {verse?.progress || 0}%
                  </span>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
};

export default VerseLibrary;